import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, ShieldOff, Copy, Trash2, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFactorSection() {
  const [status, setStatus] = useState(null);   // {enabled, can_enable, current_device_trusted, ...}
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);

  // Setup flow
  const [setupData, setSetupData] = useState(null); // {otpauth_uri, secret}
  const [setupCode, setSetupCode] = useState("");

  // Backup codes shown ONCE right after enable/regenerate
  const [backupCodes, setBackupCodes] = useState(null);

  // Disable confirmation
  const [disableCode, setDisableCode] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  // Regenerate backup
  const [regenCode, setRegenCode] = useState("");
  const [showRegen, setShowRegen] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/auth/2fa/status`);
      setStatus(r.data);
      if (r.data.enabled && r.data.can_enable) {
        const dv = await axios.get(`${API}/auth/2fa/trusted-devices`);
        setDevices(dv.data.devices || []);
      } else {
        setDevices([]);
      }
    } catch (e) {
      toast.error("Nu am putut încărca statusul 2FA: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(r.data);
      setSetupCode("");
    } catch (e) {
      toast.error("Nu pot porni setup 2FA: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/confirm`, { code: setupCode.trim() });
      setBackupCodes(r.data.backup_codes || []);
      setSetupData(null);
      setSetupCode("");
      toast.success("2FA activat");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cod invalid");
    } finally { setBusy(false); }
  };

  const doDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: disableCode.trim() });
      setShowDisable(false); setDisableCode("");
      toast.success("2FA dezactivat");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cod invalid");
    } finally { setBusy(false); }
  };

  const doRegenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/regenerate-backup-codes`, { code: regenCode.trim() });
      setBackupCodes(r.data.backup_codes || []);
      setShowRegen(false); setRegenCode("");
      toast.success("Coduri de rezervă noi generate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cod invalid");
    } finally { setBusy(false); }
  };

  const revokeDevice = async (id) => {
    if (!window.confirm("Revoci acest dispozitiv? Data viitoare când se loghează de acolo va fi cerut cod 2FA.")) return;
    try {
      await axios.delete(`${API}/auth/2fa/trusted-devices/${id}`);
      toast.success("Dispozitiv revocat");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const clearDevices = async () => {
    if (!window.confirm("Ștergi tot istoricul de dispozitive? Rămâne doar dispozitivul de acum; pe celelalte se va cere din nou cod 2FA.")) return;
    try {
      const r = await axios.delete(`${API}/auth/2fa/trusted-devices`);
      toast.success(`Am șters ${r.data.removed} intrări`);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const copyBackupCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n"))
      .then(() => toast.success("Coduri copiate în clipboard"))
      .catch(() => toast.error("Nu am putut copia"));
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  if (!status) return null;
  if (!status.can_enable) return null; // Only owner sees the section.

  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="two-factor-section">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${status.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-700/40 text-zinc-400"}`}>
            {status.enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-zinc-100">Autentificare cu 2 factori (2FA)</h2>
            <p className="text-xs text-zinc-500">
              {status.enabled
                ? "Activ · Se solicită cod din aplicația de autentificare la login de pe dispozitive noi."
                : "Dezactivat · Doar parola este necesară pentru login. Recomandăm activarea 2FA."}
            </p>
          </div>
        </div>
        <span
          data-testid="two-factor-status-badge"
          className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
            status.enabled
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-zinc-700 bg-zinc-800 text-zinc-400"
          }`}
        >
          {status.enabled ? "ACTIV" : "INACTIV"}
        </span>
      </div>

      {/* Setup flow */}
      {!status.enabled && !setupData && (
        <button
          onClick={startSetup}
          disabled={busy}
          data-testid="two-factor-activate-btn"
          className="rounded-lg bg-emerald-500/20 px-3 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50"
        >
          Activează 2FA
        </button>
      )}

      {!status.enabled && setupData && (
        <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/70 p-4" data-testid="two-factor-setup-panel">
          <div>
            <h3 className="text-sm font-medium text-zinc-100">1. Scanează codul QR</h3>
            <p className="text-[11px] text-zinc-500">Deschide Google Authenticator / Authy / 1Password și scanează.</p>
          </div>
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
            <div className="rounded-lg bg-white p-3">
              <QRCodeSVG value={setupData.otpauth_uri} size={180} level="M" />
            </div>
            <div className="flex-1 text-[11px] text-zinc-400">
              <p className="mb-1 uppercase tracking-wider text-zinc-500">Sau introdu manual codul:</p>
              <code className="block break-all rounded bg-zinc-900 px-2 py-1 font-mono text-[11px] text-zinc-200" data-testid="two-factor-manual-secret">{setupData.secret}</code>
              <p className="mt-2 text-zinc-500">Cont: <span className="text-zinc-300">BragusApp</span></p>
            </div>
          </div>
          <form onSubmit={confirmSetup} className="space-y-2">
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500">2. Introdu codul de 6 cifre din aplicație</label>
            <div className="flex gap-2">
              <input
                data-testid="two-factor-confirm-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                placeholder="123456"
                className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-lg tracking-widest text-zinc-100 outline-none focus:border-emerald-500/50"
              />
              <button
                type="submit"
                disabled={busy || setupCode.length < 6}
                data-testid="two-factor-confirm-btn"
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
              >
                {busy ? "..." : "Confirmă"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Backup codes shown ONCE */}
      {backupCodes && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4" data-testid="two-factor-backup-codes-panel">
          <div className="mb-2 flex items-center gap-2 text-amber-300">
            <AlertCircle className="h-4 w-4" />
            <h3 className="text-sm font-semibold">Salvează aceste coduri de rezervă acum</h3>
          </div>
          <p className="mb-3 text-[11px] text-amber-200/80">
            Fiecare cod poate fi folosit <strong>o singură dată</strong> dacă pierzi accesul la aplicația de autentificare.
            NU vor mai fi afișate niciodată. Copiază-le în manager-ul de parole.
          </p>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-[12px] text-zinc-100 md:grid-cols-5">
            {backupCodes.map((c) => (
              <div key={c} className="rounded bg-zinc-900/70 px-2 py-1 text-center">{c}</div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={copyBackupCodes}
              data-testid="two-factor-copy-backup"
              className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800 px-2.5 py-1 text-[11px] text-zinc-200 hover:bg-zinc-700"
            >
              <Copy className="h-3 w-3" /> Copiază tot
            </button>
            <button
              onClick={() => setBackupCodes(null)}
              data-testid="two-factor-dismiss-backup"
              className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-200 hover:bg-emerald-500/20"
            >
              <CheckCircle2 className="h-3 w-3" /> Le-am salvat
            </button>
          </div>
        </div>
      )}

      {/* Trusted devices */}
      {status.enabled && (
        <div className="space-y-4">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium text-zinc-100">Dispozitive de încredere</h3>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-zinc-500" data-testid="trusted-devices-count">{devices.length} · un rând per dispozitiv</span>
                {devices.length > 1 && (
                  <button onClick={clearDevices} data-testid="trusted-devices-clear-btn"
                    className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] text-rose-300 hover:bg-rose-500/20">
                    <Trash2 className="h-3 w-3" /> Șterge istoricul vechi
                  </button>
                )}
              </div>
            </div>
            {devices.length === 0 ? (
              <p className="text-[11px] text-zinc-500">Niciun dispozitiv memorat momentan.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-zinc-800">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900/40 text-[10px] uppercase text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Dispozitiv</th>
                      <th className="px-3 py-2 text-left">IP</th>
                      <th className="px-3 py-2 text-left">Ultima verificare</th>
                      <th className="px-3 py-2 text-right">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/30" data-testid={`trusted-device-${d.id}`}>
                        <td className="px-3 py-2 text-zinc-100">
                          {d.label || "—"}
                          {d.is_current && (
                            <span className="ml-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-medium text-emerald-300">acum</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{d.ip}</td>
                        <td className="px-3 py-2 text-[11px] text-zinc-400">{fmtDate(d.last_verified_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => revokeDevice(d.id)}
                            data-testid={`revoke-device-${d.id}`}
                            className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400"
                            title="Revocă dispozitivul"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Danger zone */}
          <div className="flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
            <button
              onClick={() => setShowRegen((v) => !v)}
              data-testid="two-factor-regen-toggle"
              className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800/50 px-3 py-1.5 text-[11px] text-zinc-200 hover:bg-zinc-800"
            >
              <RefreshCw className="h-3 w-3" /> Regenerează codurile de rezervă
            </button>
            <button
              onClick={() => setShowDisable((v) => !v)}
              data-testid="two-factor-disable-toggle"
              className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-300 hover:bg-rose-500/20"
            >
              <ShieldOff className="h-3 w-3" /> Dezactivează 2FA
            </button>
          </div>

          {showRegen && (
            <form onSubmit={doRegenerate} className="rounded-lg border border-zinc-800 bg-zinc-950/70 p-3">
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Cod curent din aplicație</label>
              <div className="flex gap-2">
                <input
                  data-testid="two-factor-regen-code"
                  required
                  value={regenCode}
                  onChange={(e) => setRegenCode(e.target.value)}
                  placeholder="123456"
                  className="flex-1 rounded border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="two-factor-regen-submit"
                  className="rounded bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  Regenerează
                </button>
              </div>
            </form>
          )}

          {showDisable && (
            <form onSubmit={doDisable} className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3">
              <p className="mb-2 text-[11px] text-rose-200">
                Vei rămâne cu login doar pe parolă. Recomandăm doar dacă ai pierdut aplicația și nu ai coduri de rezervă.
              </p>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Cod curent (sau cod de rezervă)</label>
              <div className="flex gap-2">
                <input
                  data-testid="two-factor-disable-code"
                  required
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder="123456"
                  className="flex-1 rounded border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="two-factor-disable-submit"
                  className="rounded bg-rose-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  Confirmă dezactivarea
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
